import { ChevronRight, ClipboardList, IdCard, Pencil, Plus } from "lucide-react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader } from "@/components/common/loader";
import { ClientRowActions } from "@/features/clients/components/client-row-actions";
import { useClient } from "@/features/clients/hooks/use-client";
import type { Client } from "@/features/clients/types";
import { cn } from "@/lib/utils";

interface ClientTab {
  label: string;
  /** Omitted for tabs that don't have a page yet — renders as a disabled placeholder. */
  path?: string;
}

const clientTabs: ClientTab[] = [
  { label: "Overview", path: "overview" },
  { label: "Profile", path: "profile" },
  { label: "Income Comparison", path: "comparison" },
  { label: "Cases", path: "cases" },
  { label: "IRS Forms", path: "irs-forms" },
  { label: "Documents", path: "documents" },
  { label: "Required Docs", path: "required-docs" },
  { label: "Last Year Docs", path: "last-year-docs" },
  { label: "Banking", path: "banking" },
  { label: "Financials", path: "financials" },
  { label: "Invoices", path: "invoices" },
  { label: "Transcripts", path: "transcripts" },
  { label: "IRS Notices", path: "irs-notices" },
  { label: "Letters", path: "letters" },
  { label: "E-Signatures", path: "esignatures" },
  { label: "IRS e-Filing", path: "efiling" },
  { label: "Tax Relief Report", path: "tax-relief-report" },
];

function initials(name: string) {
  return (
    name
      .split(" ")
      .map((part) => part[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

export type ClientOutletContext = {
  client: Client;
  /** Replace the active client after an update (e.g. a profile save) so this
   * layout's header reflects the change without a refetch. */
  onClientUpdated: (client: Client) => void;
};

export function useActiveClient() {
  return useOutletContext<ClientOutletContext>();
}

/**
 * Shell for every /client/:id/* page (Overview, Profile, Cases, ...): the
 * client-context header card — avatar + identity + actions + the scrollable
 * section tabs, from the "Client profile" Calm Studio mockup — shared across
 * all of a client's sub-pages. The client comes straight from the :id route
 * param, so every sub-page is independently bookmarkable/refreshable — bounces
 * back to /clients if the id doesn't match a known client.
 */
export function ClientLayout() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { client, loading, error, notFound, setClient } = useClient(id);

  if (loading) {
    return <Loader className="mt-10" />;
  }

  if (!client) {
    return (
      <div className="mx-auto mt-16 flex max-w-md flex-col items-center gap-4 text-center">
        <h2 className="text-foreground text-xl font-bold">
          {notFound ? "Client not found" : "Couldn't load this client"}
        </h2>
        <p className="text-muted-foreground text-sm">
          {notFound
            ? "This client may have been removed, or you don't have access to it."
            : (error ?? "Something went wrong.")}
        </p>
        <Button asChild variant="outline">
          <Link to="/clients">Back to clients</Link>
        </Button>
      </div>
    );
  }

  const assigned = client.assignedMembers?.length
    ? client.assignedMembers.map((m) => m.name).join(", ")
    : "Unassigned";
  const statusMeta =
    client.status === "INACTIVE"
      ? { dot: "bg-muted-foreground", label: "Inactive" }
      : client.status === "ONBOARDING"
        ? { dot: "bg-warning", label: "Onboarding" }
        : { dot: "bg-success", label: "Active" };

  // The Overview tab gets the large hero header; every other sub-tab gets a
  // compact context header (smaller avatar, no SSN line, no "Create case").
  const compact = !pathname.endsWith("/overview");

  return (
    <div className="flex flex-col gap-6">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="text-muted-foreground flex items-center gap-2 text-[13px]">
        <Link to="/clients" className="hover:text-foreground font-medium">
          Clients
        </Link>
        <ChevronRight className="text-border size-3.5" />
        <span aria-current="page" className="text-foreground truncate font-semibold">
          {client.name}
        </span>
      </nav>

      {/* Client header card — identity + actions, with the section tabs inside. */}
      <Card className="gap-0 overflow-hidden p-0">
        <div
          className={cn(
            "flex flex-col justify-between gap-5 lg:flex-row lg:items-center lg:gap-6",
            compact ? "px-6 py-4" : "p-6",
          )}
        >
          <div className={cn("flex min-w-0 items-center", compact ? "gap-4" : "gap-5")}>
            <div
              className={cn(
                "bg-brand-panel font-heading relative flex shrink-0 items-center justify-center font-bold tracking-tight text-white",
                compact ? "size-12 rounded-[15px] text-[17px]" : "size-[72px] rounded-xl text-2xl",
              )}
            >
              {initials(client.name)}
              <span
                aria-label={statusMeta.label}
                className={cn(
                  "border-card absolute rounded-full border-[3px]",
                  compact ? "-right-0.5 -bottom-0.5 size-3.5" : "-right-1 -bottom-1 size-[18px]",
                  statusMeta.dot,
                )}
              />
            </div>
            <div className="min-w-0">
              <div className={cn("flex flex-wrap items-center gap-3", compact ? "mb-1" : "mb-2")}>
                {compact ? (
                  <h1 className="text-xl font-semibold tracking-tight">
                    <Link to={`/client/${client.id}/overview`} className="hover:underline">
                      {client.name}
                    </Link>
                  </h1>
                ) : (
                  <h1 className="text-[30px] leading-tight font-semibold tracking-tight">
                    {client.name}
                  </h1>
                )}
                <Badge className="bg-accent text-info border-transparent text-[11px] font-semibold tracking-wider uppercase">
                  {client.type}
                </Badge>
              </div>
              <div className="text-muted-foreground flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px]">
                {!compact && (
                  <span className="flex items-center gap-1.5 tabular-nums">
                    <IdCard className="size-4" /> {client.idLabel}
                  </span>
                )}
                <span>
                  Case status <strong className="text-foreground font-semibold">{client.irsStatus}</strong>
                </span>
                <span>
                  Assigned <strong className="text-foreground font-semibold">{assigned}</strong>
                </span>
                <span>
                  Practitioner{" "}
                  <strong className="text-foreground font-semibold">{client.practitioner || "—"}</strong>
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" className={compact ? "h-10" : "h-[42px]"}>
              <Link to={`/client/${client.id}/profile`}>
                <Pencil /> Edit
              </Link>
            </Button>
            <Button asChild variant="outline" className={compact ? "h-10" : "h-[42px]"}>
              <Link to={`/client/${client.id}/tasks`}>
                <ClipboardList /> Add task
              </Link>
            </Button>
            {!compact && (
              <Button asChild variant="cta" className="h-[42px]">
                <Link to={`/client/${client.id}/cases`}>
                  <Plus /> Create case
                </Link>
              </Button>
            )}
            <ClientRowActions
              client={client}
              triggerVariant="outline"
              onChanged={() => navigate("/clients")}
            />
          </div>
        </div>

        {/* Section tabs — navy active pill, horizontally scrollable. */}
        <nav
          aria-label="Client sections"
          className="flex items-center gap-1 overflow-x-auto border-t px-5 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {clientTabs.map((tab) =>
            tab.path ? (
              <NavLink
                key={tab.label}
                to={`/client/${client.id}/${tab.path}`}
                className={({ isActive }) =>
                  cn(
                    "flex h-9 shrink-0 items-center rounded-[10px] px-3.5 text-[13px] font-medium transition-colors",
                    isActive
                      ? "bg-brand-panel font-semibold text-white"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted",
                  )
                }
              >
                {tab.label}
              </NavLink>
            ) : (
              <span
                key={tab.label}
                title="Coming soon"
                className="text-muted-foreground/50 flex h-9 shrink-0 cursor-not-allowed items-center rounded-[10px] px-3.5 text-[13px] font-medium"
              >
                {tab.label}
              </span>
            ),
          )}
        </nav>
      </Card>

      <Outlet context={{ client, onClientUpdated: setClient } satisfies ClientOutletContext} />
    </div>
  );
}
